/**
 * Navigation on behalf of a Fruit Slice.
 *
 * A slice cannot navigate by itself. The iframe is sandboxed with only
 * `allow-scripts` — no `allow-top-navigation`, no `allow-popups` — so `<a href>`
 * and `target="_blank"` are inert inside it, and the CSP's `form-action 'none'`
 * closes the form route. The slice therefore asks the host to navigate for it.
 *
 * Destinations are limited to Grove's own routes, deliberately. `default-src
 * 'none'` in the slice CSP exists so a slice can't ship member data off-box, and
 * navigation to an arbitrary host would hand that straight back through the
 * query string — a slice could read `memberships.list` and append it to a URL.
 * Restricting external destinations to an allowlist of vendors would bound that
 * but not close it, since the payload in the URL stays slice-controlled, so
 * external links are left out until there's a use case worth the trade.
 *
 * Everything here is synchronous so it runs inside the transient user activation
 * that propagates from the click in the slice up to this window. An `await`
 * before a `window.open` would risk the popup blocker.
 */

import { type Href, router } from 'expo-router';

import { resolveInternalPath } from './navigationTargets';

/**
 * A Grove route can't tabnab its own opener, so this skips `noopener` — which
 * would make `window.open` return null even on success — and uses the return
 * value to catch a blocked popup.
 */
const openGroveTab = (path: string): void => {
    const opened = window.open(path, '_blank');

    if (opened == null) {
        throw new Error(
            'the browser blocked opening a new tab — allow pop-ups for Grove, or drop newTab to navigate in place',
        );
    }
};

/** Navigates Grove itself to one of its own routes. */
export const goToInternalPath = (path: string, newTab: boolean): void => {
    const resolved = resolveInternalPath(path, window.location.origin);

    if (newTab) {
        openGroveTab(resolved);
        return;
    }

    // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion -- Typed routes narrow `Href` to known literals, but a slice supplies a path at runtime; an unknown route falls through to Grove's +not-found rather than failing here.
    router.navigate(resolved as Href);
};

/**
 * Grove's current route as the host window sees it — not the iframe. `path` is
 * the same shape `navigation.go` accepts (pathname + search + hash).
 */
export const getCurrentGroveLocation = (): {
    path: string;
    pathname: string;
    search: string;
    hash: string;
} => {
    const { pathname, search, hash } = window.location;
    return {
        path: `${pathname}${search}${hash}`,
        pathname,
        search,
        hash,
    };
};
